<template>
  <div class="category">
    <Head
      :title="page.title"
      :description="page.content"
    />
    <div class="container">
      <SearchBar
        v-if="$store.state.layout === 'mobile'" class="searchbar" show
        inline
      />
      <Breadcrumb
        :breadcrumb="blogBreadcrumb"
        class="blog-breadcrumb"
      />
      <FeaturedPosts
        v-if="page.featured.length"
        :results="page.featured"
      />
      <FeaturedCategories
        v-if="page.subcategories.length"
        :results="page.subcategories"
      />
    </div>
    <div class="blog-banner-wrap">
      <div class="banner col col-6">
        <p class="h2">
          Start Your Data Science Career
        </p>
        <p class="descr body-1">
          {{ darkPreFooterDescription }}
        </p>
        <nuxt-link
          to="/signup/"
          class="btn-primary"
        >
          Start Today
        </nuxt-link>
        <p class="body-1">
          Join <b>{{ $c.text('students.count') }}+</b> students
        </p>
        <p class="students">
          <img src="../../assets/images/users-group-big.png" alt="users-group">
        </p>
      </div>
    </div>
    <template v-if="page.subcategories.length">
      <Lazy never>
        <SubCategories
          :results="page.subcategories"
        />
      </Lazy>
    </template>
    <template v-else>
      <AllPosts
        :blog-breadcrumb="blogBreadcrumb"
        :page="page"
        :categories="[{ id: page.categoryId }]"
      />
    </template>
    <Lazy never>
      <PreFooter
        title="Ready to Start Learning?"
        description="Choose your career track, build in-demand skills, and land a job in data science."
        button-title="Start Now"
        button-link="/signup/"
        class="blog-pre-footer"
      />
    </Lazy>
  </div>
</template>
<script>
import Lazy from 'vue-lazy-hydration';
import global from '@itbpbg/lms-components/src/mixins/global';
import Breadcrumb from '@itbpbg/lms-components/src/components/Breadcrumb';
import jsonld from '@itbpbg/lms-components/src/utils/json-ld-ds';
import SearchBar from './Search/SearchBar';
import Head from './Head';
import FeaturedPosts from './FeaturedPosts';
import FeaturedCategories from './FeaturedCategories';
import SubCategories from './SubCategories';
import AllPosts from './AllPosts';
import PreFooter from '../../components/PreFooter';

export default {
  name: 'CategoryPage',
  components: {
    Lazy, Head, Breadcrumb, SearchBar, FeaturedPosts, FeaturedCategories, SubCategories, AllPosts, PreFooter,
  },
  mixins: [global],
  props: {
    info: { type: Object, required: true },
  },
  data() {
    return {
      page: { ...this.info, metaTitle: `${this.info.metaTitle}${+this.$route.query.page > 1 ? ` - Page ${this.$route.query.page}` : ''}` },
      metaTitle: `${this.info.metaTitle}${+this.$route.query.page > 1 ? ` - Page ${this.$route.query.page}` : ''}`,
      blogBreadcrumb: [],
    };
  },
  head() {
    let head = {};

    if (this.page.subcategories.length) {
      const datePublished = this.dateIsoString(this.page.created);
      const dateModified = this.dateIsoString(this.page.updated);
      const searchAction = jsonld.PotentialActions.search;
      searchAction.target = [`${this.$config.siteUrl}/blog/search?q={search_term_string}`];
      this.page.fullPath = `${this.$config.siteUrl}${this.$route.fullPath}`;

      const seoWebPage = {
        ...jsonld.WebPage,
        url: this.page.fullPath,
        name: this.page.metaTitle,
        description: this.page.metaDescription,
        datePublished,
        dateModified,
        potentialAction: [searchAction],
      };

      const seoWebSite = { ...jsonld.WebSite };

      jsonld.CollectionPage.mainEntity.itemListElement = [];
      if (this.page.subcategories) {
        this.page.subcategories.forEach((item) => {
          const post = {
            '@type': 'ItemPage',
            name: item.category,
          };
          jsonld.CollectionPage.mainEntity.itemListElement.push(post);
        });
      }

      const jsonData = [
        {
          '@context': 'https://schema.org',
          '@graph': [seoWebSite, seoWebPage, jsonld.CollectionPage],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: jsonld.JSONBreadcrumb(this.blogBreadcrumb, this.$config.siteUrl),
        },
      ];

      head = {
        titleTemplate: `${this.page.metaTitle}%s`,
        link: [
          { rel: 'canonical', href: this.page.canonicalUrl || this.canonicalURL() },
        ],
        meta: this.metaProps({ ...this.page, isBlog: true }),
        script: [
          {
            vmid: 'jsonld',
            type: 'application/ld+json',
            json: jsonData,
          },
        ],
      };
    }

    return head;
  },
  computed: {
    darkPreFooterDescription() {
      if (this.$route.fullPath.includes('expert-interviews') || this.$route.fullPath.includes('job-interview-tips')) {
        return 'Acquire the theory and practical skills you need to break into data science.';
      }
      return 'Gain the theory and practical skills you need to break into data science.';
    },
  },
  watch: {
    $route(v) {
      this.metaTitle = this.info.title;
      if (+v.query.page > 1) {
        this.metaTitle += ` - Page ${v.query.page}`;
      }
    },
  },
  created() {
    this.blogBreadcrumb = this.breadcrumb(this.page, 'blogCategory');
  },
};

</script>

<style scoped lang="scss">
.category {
  .blog-breadcrumb {
    margin-top: var(--spacing-100);
  }

  .blog-banner-wrap {
    height: 467px;
    background: var(--main-dark-shade-1);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: calc(var(--spacing-120) - var(--spacing-30)) 0 0;

    .banner {
      text-align: center;

      p {
        margin: 0;
      }

      .h2 {
        color: var(--main-light);
        margin-bottom: var(--spacing-16);
      }

      .body-1 {
        color: var(--main-light-shade-4);
        width: 80%;
        margin: var(--spacing-16) auto var(--spacing-8);
      }

      .descr {
        margin: 0 auto var(--spacing-48);
      }

      .btn-primary {
        width: 300px;
        padding: var(--spacing-20);
      }
    }
  }

  .subcategories {
    padding-bottom: var(--spacing-120);

    .subcategory {
      margin-top: var(--spacing-80);

      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid rgba(0, 12, 31, 0.12);
        padding-bottom: var(--spacing-16);
        margin-bottom: var(--spacing-24);

        h3 {
          margin: 0;
        }
      }

      &:first-of-type {
        margin-top: var(--spacing-100);
      }
    }
  }
}

@include bp($bp-laptop) {
  .category .blog-banner-wrap .banner .btn-primary {
    width: 225px;
  }
}

@include bp($bp-mobile) {
  .category {
    .blog-breadcrumb {
      margin-top: var(--spacing-36);
    }

    .blog-banner-wrap .banner {
      .h2 {
        font-size: 32px;
      }

      .btn-primary {
        width: 328px;
      }
    }
  }
}
</style>
